import { isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SvgService } from '@core/services';

@Directive({
  selector: '[b2cSvg]',
})
export class SvgDirective {
  svgPath = input.required<string>();

  fill = input<string>('');
  width = input<string>('');
  height = input<string>('');
  stroke = input<string>('');
  alt = input<string>('');

  private readonly el: ElementRef = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly platformId: object = inject(PLATFORM_ID);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly svgService: SvgService = inject(SvgService);

  private currentSvgElement: SVGElement | null = null;

  constructor() {
    effect(() => {
      const path = this.svgPath();
      if (path && isPlatformBrowser(this.platformId)) {
        this.loadSvg(path);
      }
    });
  }

  private loadSvg(url: string): void {
    this.svgService
      .fetch(url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((svgtext: string) => {
        this.processSvg(
          svgtext,
          this.width(),
          this.height(),
          this.fill(),
          this.stroke(),
          this.alt()
        );
      });
  }

  private processSvg(
    svgText: string,
    width: string,
    height: string,
    fill: string,
    stroke: string,
    alt: string
  ): void {
    this.cleanupPreviousSvg(); // <- added

    const parser: DOMParser = new DOMParser();
    const doc: Document = parser.parseFromString(svgText, 'image/svg+xml');
    const svgElement: SVGElement = doc.documentElement as unknown as SVGElement;

    if (width) this.renderer.setAttribute(svgElement, 'width', width);
    if (height) this.renderer.setAttribute(svgElement, 'height', height);
    if (alt) this.renderer.setAttribute(svgElement, 'aria-label', alt);

    this.renderer.setAttribute(svgElement, 'role', 'img');
    this.renderer.setAttribute(svgElement, 'data-cy', 'loaded_svg');

    const children = svgElement.children;
    for (let i = 0; i < children.length; i++) {
      if (fill) this.renderer.setAttribute(children[i], 'fill', fill);
      if (stroke && children[i].hasAttribute('stroke')) {
        this.renderer.setAttribute(children[i], 'stroke', stroke);
      }
    }

    this.renderer.appendChild(this.el.nativeElement, svgElement);
    this.currentSvgElement = svgElement;
  }

  private cleanupPreviousSvg(): void {
    if (this.currentSvgElement) {
      this.renderer.removeChild(this.el.nativeElement, this.currentSvgElement);
      this.currentSvgElement = null;
    }
  }
}
